import { Outlet } from "react-router";
import {
  LogoutButton,
  NavbarLink,
  NavbarTextWithIndicator,
} from "./NavComponents";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import {
  generalDataAtom,
  hostDataAtom,
  loginStatusAtom,
  modelDataAtom,
  userAtom,
} from "../atoms";
import apiAgent from "../api/apiAgent";
import { checkTitle, transformModelSchema } from "../utils";
import { IModelData } from "../atoms";
import { Loader } from "../components/Loader";
import { getPublishStates } from "../components/Publishing/utils";
import {
  publishAttractorAtom,
  publishContentNodeAtom,
  publishProfileNodeAtom,
  publishVideoNodeAtom,
} from "../components/Publishing/atoms";
import { checkSingleItemSchemas } from "../utils/singleItemsConfig";

/**
 * Modify this fo adjust menu items
 * @returns Navbar Menu
 */

const NavbarLayout = () => {
  const setModelData = useSetAtom(modelDataAtom);
  const setHostData = useSetAtom(hostDataAtom);
  const generalData = useAtomValue(generalDataAtom);
  const user = useAtomValue(userAtom);
  const [loginStatus, setLoginStatus] = useAtom(loginStatusAtom);
  const [modelsLoaded, setModelsLoaded] = useState<boolean>(false);
  const [hostDataLoaded, setHostDataLoaded] = useState<boolean>(false);

  const [pnPublished, setPnPublished] = useAtom(publishProfileNodeAtom);
  const [vnPublished, setVnPublished] = useAtom(publishVideoNodeAtom);
  const [cnPublished, setCnPublished] = useAtom(publishContentNodeAtom);
  const [attractPublished, setAttractPublished] = useAtom(publishAttractorAtom);

  useEffect(() => {
    if (loginStatus === true) {
      if (!modelsLoaded) fetchModelData();
      if (!hostDataLoaded) fetchHostData();
      checkSingleItemSchemas();
      // TODO: Get publish states here like this:
      getPublishStates("profile_node", setPnPublished);
      getPublishStates("video_node", setVnPublished);
      getPublishStates("content_node", setCnPublished);
      getPublishStates("attractor", setAttractPublished);
    }
  }, [loginStatus]);

  const fetchHostData = async () => {
    try {
      const response = await apiAgent.Hosting.get();
      // console.log(hostResponse);
      if (response.status === 200) {
        // console.log("Host data loaded");
        setHostData(response.data);
        setHostDataLoaded(true);
      } else if (response.status === 401) {
        setLoginStatus(false);
      }
    } catch (err) {
      console.log("Could not set host data", err);
    }
  };

  const fetchModelData = async () => {
    try {
      const response = await apiAgent.Models.getAll();
      // console.log(response.data);
      if (response.status === 200) {
        // Since the CMS also allows managing "users" (a system model), add that here too
        const userModel: IModelData = response.data.system_models.find(
          (sm: any) => sm.name === "users"
        );
        const allModels: IModelData[] = [userModel, ...response.data.models];

        const modelData = transformModelSchema(allModels);
        // console.log("Model data loaded:", modelData);
        setModelData(modelData);
        setModelsLoaded(true);
      } else if (response.status === 401) {
        setLoginStatus(false);
      }
    } catch (err) {
      console.log(err);
      console.log("Could not load modelData");
    }
  };

  return (
    <div className="w-full h-full min-w-[60rem] flex bg-sidebarBg">
      <div className="w-[16rem] flex flex-col">
        <div className="p-8 w-full flex flex-col items-center">
          <img
            src="/assets/outback_guardians_sidebar_logo.png"
            className="w-24"
          />
          <div className="font-bold">Outback Guardians</div>
        </div>
        <NavbarLink to="/">Home</NavbarLink>
        <NavbarLink to="/m/profile_node">
          <NavbarTextWithIndicator publishObj={pnPublished}>
            {checkTitle("profile_node")}
          </NavbarTextWithIndicator>
        </NavbarLink>
        <NavbarLink to="/m/content_node">
          <NavbarTextWithIndicator publishObj={cnPublished}>
            {checkTitle("content_node")}
          </NavbarTextWithIndicator>
        </NavbarLink>
        <NavbarLink to="/m/video_node">
          <NavbarTextWithIndicator publishObj={vnPublished}>
            {checkTitle("video_node")}
          </NavbarTextWithIndicator>
        </NavbarLink>
        {/* TODO: OPTIONAL, enable access to users menu here */}
        {user?.user_role === "admin" && generalData.enable_user_management && (
          <NavbarLink to="/m/users">Manage Users</NavbarLink>
        )}
        <div className="w-full flex-grow " />
        <div className="w-full text-center text-sm pb-2">
          Logged in as <span className="font-bold">{user?.username}</span>
        </div>
        <LogoutButton />
      </div>
      <div className="w-[calc(100%-16rem)] h-full bg-white text-black">
        {modelsLoaded ? (
          <Outlet />
        ) : (
          <div className="w-full h-full text-center grid content-center text-grayText">
            <Loader />
            <div className="my-4">loading model data... Please wait</div>
          </div>
        )}
      </div>
    </div>
  );
};

export { NavbarLayout };
